/**
 * @description Modal for creating PTO requests from the dashboard
 *
 * Key features:
 * - Same fields as employee portal TimeOffScreen
 * - Leave type, date range, hours, optional reason
 * - Validation matching employee portal
 * - HeadlessUI Dialog pattern matching TimeEntryEditModal
 *
 * @dependencies
 * - timeClockService: API calls
 * - @headlessui/react: Dialog component
 */

import React, { useState, Fragment, useRef, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Calendar, AlertCircle, ChevronDown } from 'lucide-react';
import timeClockService from '../../../services/api/timeClockService';

// Leave type display configuration (matches employee portal)
const LEAVE_TYPES = {
  vacation: { label: 'Vacation', color: 'bg-blue-500' },
  sick: { label: 'Sick', color: 'bg-red-500' },
  personal: { label: 'Personal', color: 'bg-purple-500' },
  bereavement: { label: 'Bereavement', color: 'bg-gray-500' },
  jury_duty: { label: 'Jury Duty', color: 'bg-amber-500' },
  other: { label: 'Other', color: 'bg-slate-500' },
};

const TimeOffRequestModal = ({ isOpen, onClose, onSuccess }) => {
  // Form state
  const [formData, setFormData] = useState({
    leaveType: 'vacation',
    startDate: '',
    endDate: '',
    hoursRequested: '',
    reason: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  // Ref for Dialog initialFocus
  const cancelCloseRef = useRef(null);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return formData.startDate !== '' ||
      formData.endDate !== '' ||
      formData.hoursRequested !== '' ||
      formData.reason !== '';
  }, [formData]);

  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      leaveType: 'vacation',
      startDate: '',
      endDate: '',
      hoursRequested: '',
      reason: '',
    });
    setError(null);
  };

  // Handle close with unsaved changes check
  const handleClose = () => {
    if (showConfirmClose) return;
    if (hasUnsavedChanges) {
      setShowConfirmClose(true);
    } else {
      resetForm();
      onClose();
    }
  };

  // Force close without saving
  const handleForceClose = () => {
    setShowConfirmClose(false);
    resetForm();
    onClose();
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.startDate || !formData.endDate) {
      setError('Please select start and end dates');
      return;
    }

    if (!formData.hoursRequested || parseFloat(formData.hoursRequested) <= 0) {
      setError('Please enter hours requested');
      return;
    }

    if (formData.startDate > formData.endDate) {
      setError('End date cannot be before start date');
      return;
    }

    setSaving(true);
    try {
      await timeClockService.createPtoRequest({
        leaveType: formData.leaveType,
        startDate: formData.startDate,
        endDate: formData.endDate,
        hoursRequested: parseFloat(formData.hoursRequested),
        reason: formData.reason || null,
      });

      resetForm();
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error submitting PTO request:', err);
      setError(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all w-full max-w-lg max-h-[90vh] flex flex-col">
                  {/* Header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-brand-light rounded-lg">
                        <Calendar className="w-5 h-5 text-brand" />
                      </div>
                      <div>
                        <Dialog.Title as="h2" className="text-lg font-semibold text-gray-900">
                          Request Time Off
                        </Dialog.Title>
                        <p className="text-sm text-gray-500">
                          Submit a new PTO request
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleClose}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
                    <div className="px-6 py-5 space-y-5">
                      {/* Leave Type */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Leave Type <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <select
                            value={formData.leaveType}
                            onChange={(e) => setFormData({ ...formData, leaveType: e.target.value })}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand appearance-none bg-white"
                          >
                            {Object.entries(LEAVE_TYPES).map(([key, config]) => (
                              <option key={key} value={key}>
                                {config.label}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                      </div>

                      {/* Date Range */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Date Range</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Start Date <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="date"
                              value={formData.startDate}
                              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              End Date <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="date"
                              value={formData.endDate}
                              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                              min={formData.startDate}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand"
                              required
                            />
                          </div>
                        </div>
                      </div>

                      {/* Hours Requested */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Hours Requested <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          step="0.5"
                          min="0.5"
                          value={formData.hoursRequested}
                          onChange={(e) => setFormData({ ...formData, hoursRequested: e.target.value })}
                          placeholder="e.g., 8"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand placeholder-gray-400"
                          required
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Enter total hours for the requested time off
                        </p>
                      </div>

                      {/* Reason (Optional) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Reason <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                          value={formData.reason}
                          onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                          rows={2}
                          placeholder="Any additional details..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand focus:border-brand placeholder-gray-400 resize-none"
                        />
                      </div>

                      {/* Error */}
                      {error && (
                        <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-lg">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          <p className="text-sm">{error}</p>
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={saving}
                        className="px-4 py-2 text-sm bg-brand text-white rounded-lg font-medium hover:bg-brand-hover transition-colors disabled:opacity-50"
                      >
                        {saving ? 'Submitting...' : 'Submit Request'}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Confirm Close Dialog */}
      <Transition.Root show={showConfirmClose} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={() => setShowConfirmClose(false)} initialFocus={cancelCloseRef}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-[70] overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white px-4 pb-4 pt-5 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                        Unsaved Changes
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          You have unsaved changes. Are you sure you want to close without submitting?
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      ref={cancelCloseRef}
                      className="inline-flex w-full justify-center rounded-lg bg-white px-3 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition-colors duration-200 sm:col-start-1"
                      onClick={() => setShowConfirmClose(false)}
                    >
                      Keep Editing
                    </button>
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-lg bg-red-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 transition-colors duration-200 sm:col-start-2"
                      onClick={handleForceClose}
                    >
                      Discard Changes
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default TimeOffRequestModal;
